import React, { lazy, Suspense, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { ErrorBoundary } from 'react-error-boundary'
import kdsStore from '../Stores/KdsStore'
import kdsLockStore from '../Stores/KdsLockStore'
import GlobalStore from '../Stores/GlobalStore'
import { MultiErrorPopup } from '~shared/lib/ErrorPopup/MultiErrorPopup'
import { KdsSettings } from '../Components/Settings/KdsSettings'
import WeightConfirmationModal from '../Components/WeightConfirmation/WeightConfirmationModal'
import { ManageMenuModal } from '../Components/ManageMenu/ManageMenuModal'
import { AvailabilityModal } from '../Components/ActionBar/AvailabilityModal'
import { OfflineDevicesModal } from '../Components/ActionBar/OfflineDevicesModal'
import { OrderEntryHostFrame } from '../Components/OrderEntry/OrderEntryHostFrame'
import './KitchenDisplay.scss'
import { FilterStatus, LocalSettings } from '../Types/Enums'
import { useInterval } from '~shared/hooks/useInterval'
import { ActionBar } from '../Components/ActionBar/ActionBar'
import { customerOrderStatus } from '~shared/util/constants'
import { Order } from '../Types/Order'
import { OrderUtil } from '../Utils/OrderUtil'
import { useNotificationSound } from '../Hooks/useNotificationSound'
import { ErrorProvider, useError } from '~shared/hooks/useErrorHandling'
import { printOrder } from '../Services/OrderService'
import { OrdersList } from '../Components/OrdersList/OrdersList'
import { KdsAuthorizationPromptHost } from '../Components/Authorization/KdsAuthorizationPromptHost'
import { isActionAvailable, isActionElevated } from '../Utils/kdsActionPolicy'
import { KdsActionId } from '../Types/KdsAuthorization'
import { isEmbedded } from '../Utils/embedding'
import { PaymentErrorDialog } from '../Components/Docket/PaymentErrorDialog/PaymentErrorDialog'
import { useSignalR } from '../Hooks/useSignalR'
import { getApiErrorMessage } from '~shared/util/apiErrorMessage'
import { FetchStatus } from '~shared/lib/Types/FetchTypes'
import { CircleCrossIcon } from '../Assets/svg/Icons'
import { KdsLockedOverlay } from '../Components/Lock/KdsLockedOverlay'

import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isToday from 'dayjs/plugin/isToday'
import isTomorrow from 'dayjs/plugin/isTomorrow'
import isYesterday from 'dayjs/plugin/isYesterday'
import TDSDialog from '~shared/lib/TDSDialog/TDSDialog'
import { Button as TdsButton, TdsProvider } from '@instacart/tds'
import { IdsProvider } from '@instacart/ids-core'

dayjs.extend(customParseFormat)
dayjs.extend(isToday)
dayjs.extend(isTomorrow)
dayjs.extend(isYesterday)

// A fresh lazy wrapper lets a failed chunk request be retried.
const createOrderEntryMount = () => lazy(() => import('./OrderEntry/OrderEntryMount'))

type OrderEntrySession = {
  editOrderId: string | null
  stepUpToken?: string
}

const KitchenDisplayComponent = () => {
  const config = GlobalStore.configFetch.data

  const [isDepartmentOrderSettingsVisible, setIsDepartmentOrderSettingsVisible] =
    useState<boolean>(false)
  const [localSettings, setLocalSettings] = useState<LocalSettings | null>(
    JSON.parse(localStorage.getItem('departmentOrderSettings') || 'null') ?? {
      automaticPrintingEnabled: true,
      printingEnabled: true,
    }
  )
  const [activeWeightOrder, setActiveWeightOrder] = useState<Order | null>(null)
  const [weightConfirmationSource, setWeightConfirmationSource] = useState<string | null>(null)

  const [paymentErrorOrder, setPaymentErrorOrder] = useState<Order | null>(null)
  const [paymentErrorMessage, setPaymentErrorMessage] = useState<string | null>(null)
  const [manageMenuModalOpen, setManageMenuModalOpen] = useState<boolean>(false)
  const [availabilityModalOpen, setAvailabilityModalOpen] = useState(false)
  const [offlineDevicesModalOpen, setOfflineDevicesModalOpen] = useState(false)
  const [orderEntrySession, setOrderEntrySession] = useState<OrderEntrySession | null>(null)
  const [OrderEntryMount, setOrderEntryMount] = useState(createOrderEntryMount)

  const {
    initialLoad,
    filteredOrders,
    currentFilter,
    setFilter,
    fetchOrders,
    fetchDepartmentAvailability,
    fetchSiteHealth,
    offlineDeviceCount,
    departmentAvailable,
    departmentStatus,
    hasNewOrders,
  } = kdsStore

  const { dialogOpen, confirmAudioEnabled } = useNotificationSound({ active: hasNewOrders })
  const { errors, addError, removeError } = useError()
  const [printerLoading, setPrinterLoading] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const hasOrdersError = kdsStore.ordersFetch.error != null
  const isLocked = kdsLockStore.isLocked
  // Lock is standalone-only; the embedded Admin shell owns its own chrome.
  const showLockOverlay = !isEmbedded && isLocked

  const handleOrderEntryClose = () => {
    setOrderEntrySession(null)
    fetchOrders()
  }

  const handleSetFilter = (newFilter: FilterStatus) => {
    setIsLoading(true)
    setFilter(newFilter)

    Promise.resolve(fetchOrders()).finally(() => {
      setIsLoading(false)
    })
  }

  const handlePrintOrder = async (order: Order) => {
    if (!localSettings?.printingEnabled) {
      addError('Printing is not enabled. Please enable printing in settings.')
      return
    }

    try {
      setPrinterLoading(order.orderNo)
      await printOrder(order.id)
    } catch (e) {
      addError(getApiErrorMessage(e))
    } finally {
      setPrinterLoading(null)
    }
  }

  const deviceOfflineNotificationsEnabled = config?.features.deviceOfflineNotifications ?? false

  useEffect(() => {
    fetchOrders()
    fetchDepartmentAvailability()
    if (deviceOfflineNotificationsEnabled) {
      fetchSiteHealth()
    }
  }, [fetchOrders, fetchDepartmentAvailability, fetchSiteHealth, deviceOfflineNotificationsEnabled])

  // Self-heal a transient connection drop by refetching when the device comes back
  // online, rather than offering a hard reload that would white-screen while offline.
  useEffect(() => {
    const handleOnline = () => {
      if (kdsStore.ordersFetch.status === FetchStatus.error) {
        fetchOrders()
      }
    }
    window.addEventListener('online', handleOnline)
    return () => window.removeEventListener('online', handleOnline)
  }, [fetchOrders])

  useInterval(
    () => {
      fetchSiteHealth()
    },
    //polling set to 30mins as a fallback, signalR should be sending updates
    deviceOfflineNotificationsEnabled ? 1800000 : null
  )

  const ordersSignalREnabled = config?.features.signalREnabled ?? false
  useSignalR({
    enabled: ordersSignalREnabled || deviceOfflineNotificationsEnabled,
    notificationsUrl: config?.notificationsUrl,
    onOrdersUpdated: ordersSignalREnabled
      ? () => {
          fetchOrders()
        }
      : undefined,
    onSiteHealthChanged: deviceOfflineNotificationsEnabled
      ? () => {
          fetchSiteHealth()
        }
      : undefined,
  })

  // Poll when websockets are disabled.
  const pollDelayMs = !config
    ? null
    : config.features.signalREnabled
      ? null
      : currentFilter === FilterStatus.Complete
        ? 60000
        : 10000

  useInterval(() => {
    fetchOrders()
  }, pollDelayMs)

  if (!config) return null

  const updateOrderStatus = async (order: Order): Promise<void> => {
    setPaymentErrorOrder(null)
    setPaymentErrorMessage(null)

    const hasWeightItemsInThisDepartment = order.items.some(
      item => item.inThisDepartment && item.weightRequiresConfirmation
    )

    if (
      order.status === customerOrderStatus.Confirmed &&
      hasWeightItemsInThisDepartment &&
      config.features.weightConfirmationRequired
    ) {
      setWeightConfirmationSource('completeAction')
      setActiveWeightOrder(order)
      return
    }

    const nextStatus = OrderUtil.nextStatus(order, currentFilter)

    try {
      await kdsStore.updateOrder(order.id, [], nextStatus)

      const autoPrintEnabled =
        localSettings?.printingEnabled &&
        localSettings?.automaticPrintingEnabled &&
        currentFilter === FilterStatus.Active

      if (autoPrintEnabled) {
        if (nextStatus === config.orderStatuses.Confirmed) {
          const updatedOrder = kdsStore.ordersFetch.data?.find(o => o.id === order.id)
          if (updatedOrder) {
            await handlePrintOrder(updatedOrder)
          }
        } else if (
          nextStatus === config.orderStatuses.Complete &&
          config.features.reprintOnComplete
        ) {
          await handlePrintOrder(order)
        }
      }
    } catch (e: any) {
      const firstError = e?.body?.errors?.[0]
      if (firstError?.errorCode === 'PaymentError') {
        setPaymentErrorOrder(order)
        setPaymentErrorMessage(
          firstError?.context ?? firstError?.errorData1 ?? 'Unknown payment error.'
        )
        return
      }

      addError(getApiErrorMessage(e))
    }
  }

  const openOrderEntry = async (targetEditOrderId: string | null = null) => {
    let authorization: { stepUpToken?: string } | null
    try {
      authorization = await kdsStore.authorizeOrderEntry()
    } catch (e) {
      addError(getApiErrorMessage(e))
      return
    }
    if (!authorization) return

    setOrderEntryMount(createOrderEntryMount())
    setOrderEntrySession({
      editOrderId: targetEditOrderId,
      stepUpToken: authorization.stepUpToken,
    })
  }

  const handleEditOrder = (order: Order) => {
    void openOrderEntry(order.id)
  }

  const handleLock = () => {
    void kdsLockStore.lock().catch(e => addError(getApiErrorMessage(e)))
  }

  const confirmWeightAction = (order: Order) => {
    setWeightConfirmationSource('confirmWeightAction')
    setActiveWeightOrder(order)
  }

  const closeWeightConfirmationModal = () => {
    setWeightConfirmationSource(null)
    setActiveWeightOrder(null)
    fetchOrders()
  }

  const handleWeightConfirmed = async (order: Order) => {
    if (
      (config?.features.reprintOnWeightConfirmation || config?.features.reprintOnComplete) &&
      localSettings?.printingEnabled &&
      localSettings?.automaticPrintingEnabled
    ) {
      await handlePrintOrder(order)
    }
  }

  const handleSettingsSave = (newLocalSettings: LocalSettings) => {
    setLocalSettings(newLocalSettings)
    setIsDepartmentOrderSettingsVisible(false)
  }

  const canLockKds =
    !isEmbedded && isActionElevated(config.actionPolicy, KdsActionId.LockKds) && !isLocked

  return (
    <IdsProvider>
      <TdsProvider>
        <ErrorProvider errors={errors} addError={addError} removeError={removeError}>
          <KdsAuthorizationPromptHost />
          <div className="department-orders" data-testid="kds-content">
            <div className="main-panel">
              <ActionBar
                setIsDepartmentOrderSettingsVisible={setIsDepartmentOrderSettingsVisible}
                setCurrentFilter={handleSetFilter}
                currentFilter={currentFilter}
                orders={kdsStore.ordersFetch.data ?? []}
                departmentAvailable={departmentAvailable}
                departmentStatus={departmentStatus}
                canSetDepartmentAvailability={isActionAvailable(
                  config.actionPolicy,
                  KdsActionId.SetDepartmentAvailability
                )}
                itemAvailabilityEnabled={
                  config.features.itemAvailability &&
                  isActionAvailable(config.actionPolicy, KdsActionId.EditItemAvailability)
                }
                setManageMenuOpen={() => setManageMenuModalOpen(true)}
                setAvailabilityModalOpen={setAvailabilityModalOpen}
                setOfflineDevicesModalOpen={setOfflineDevicesModalOpen}
                offlineDeviceCount={offlineDeviceCount}
                deviceOfflineNotificationsEnabled={deviceOfflineNotificationsEnabled}
                enableKDSOrderEntry={config.features.kdsOrderEntry}
                canAddKdsOrders={isActionAvailable(config.actionPolicy, KdsActionId.AddKdsOrders)}
                onAddOrder={() => void openOrderEntry()}
                canLockKds={canLockKds}
                onLock={handleLock}
              />
              {initialLoad || isLoading ? (
                <div className="loading-container" data-testid="loading-component">
                  <div className="fs-loading-large"></div>
                </div>
              ) : (
                <OrdersList
                  filteredOrders={filteredOrders}
                  config={config}
                  localSettings={localSettings}
                  printerLoading={printerLoading ?? ''}
                  printOrder={order => handlePrintOrder(order)}
                  updateOrderStatus={updateOrderStatus}
                  confirmWeightAction={confirmWeightAction}
                  numberOfActiveOrdersRequiringConfirmation={
                    kdsStore.numberOfActiveOrdersRequiringConfirmation
                  }
                  numberOfUpcomingOrdersRequiringConfirmation={
                    kdsStore.numberOfUpcomingOrdersRequiringConfirmation
                  }
                  currentFilter={currentFilter}
                  setFilter={handleSetFilter}
                  fetchOrders={fetchOrders}
                  onEditOrder={
                    config.features.kdsOrderEntry &&
                    isActionAvailable(config.actionPolicy, KdsActionId.AddKdsOrders)
                      ? handleEditOrder
                      : undefined
                  }
                />
              )}
            </div>

            {!showLockOverlay && hasOrdersError && (
              <TDSDialog
                header="Unable to Load Orders"
                toggleDialog={() => undefined}
                showCloseIcon={false}
                customClass="orders-error-dialog-modal"
                action={actionFinished => {
                  void Promise.resolve(fetchOrders()).finally(actionFinished)
                }}
                actionButtonLabel="Retry"
              >
                <div className="orders-error-dialog" role="alert">
                  <CircleCrossIcon className="orders-error-dialog-icon" />
                  <p data-testid="orders-error">
                    {kdsStore.ordersFetch.error ?? 'There was an error loading orders.'}
                  </p>
                </div>
              </TDSDialog>
            )}

            {!showLockOverlay && dialogOpen && (
              <TDSDialog
                toggleDialog={confirmAudioEnabled}
                action={confirmAudioEnabled}
                header="Audio Notifications"
                actionButtonLabel="Continue"
              >
                <p>Please ensure that your device's audio is set to its maximum volume.</p>
              </TDSDialog>
            )}

            {!showLockOverlay && activeWeightOrder && (
              <WeightConfirmationModal
                order={activeWeightOrder}
                close={closeWeightConfirmationModal}
                weightConfirmationSource={weightConfirmationSource!}
                onWeightConfirmed={handleWeightConfirmed}
              />
            )}

            {!showLockOverlay && paymentErrorOrder && paymentErrorMessage && (
              <PaymentErrorDialog
                order={paymentErrorOrder}
                paymentError={paymentErrorMessage}
                onDialogClose={() => {
                  setPaymentErrorOrder(null)
                  setPaymentErrorMessage(null)
                }}
              />
            )}

            {!showLockOverlay && isDepartmentOrderSettingsVisible && (
              <KdsSettings
                onSettingsSave={handleSettingsSave}
                cancel={() => setIsDepartmentOrderSettingsVisible(false)}
                initialPrintingEnabled={localSettings?.printingEnabled}
                initialAutomaticPrintingEnabled={localSettings?.automaticPrintingEnabled}
              />
            )}

            {!showLockOverlay && manageMenuModalOpen && (
              <ManageMenuModal
                setManageMenuModalOpen={setManageMenuModalOpen}
                siteId={config.siteId}
                departmentId={config.departmentId}
              />
            )}

            {!showLockOverlay && availabilityModalOpen && (
              <AvailabilityModal
                setAvailabilityModalOpen={setAvailabilityModalOpen}
                canAdjustDepartmentPrepTime={isActionAvailable(
                  config.actionPolicy,
                  KdsActionId.AdjustDepartmentPrepTime
                )}
              />
            )}

            {!showLockOverlay && offlineDevicesModalOpen && (
              <OfflineDevicesModal setOfflineDevicesModalOpen={setOfflineDevicesModalOpen} />
            )}

            {!showLockOverlay && <MultiErrorPopup errors={errors} removeError={removeError} />}

            {!showLockOverlay && orderEntrySession && (
              <OrderEntryHostFrame onClose={handleOrderEntryClose}>
                <ErrorBoundary
                  fallbackRender={({ resetErrorBoundary }) => (
                    <div className="oe-host-frame__message" role="alert">
                      <h2>Order entry failed to load</h2>
                      <p>Try loading order entry again or return to the kitchen display.</p>
                      <TdsButton
                        variant="primary"
                        onClick={() => {
                          setOrderEntryMount(createOrderEntryMount())
                          resetErrorBoundary()
                        }}
                      >
                        Try Again
                      </TdsButton>
                    </div>
                  )}
                >
                  <Suspense
                    fallback={
                      <div className="oe-host-frame__loading">
                        <div className="fs-loading-large" />
                      </div>
                    }
                  >
                    <OrderEntryMount
                      onClose={handleOrderEntryClose}
                      editOrderId={orderEntrySession.editOrderId}
                      stepUpToken={orderEntrySession.stepUpToken}
                    />
                  </Suspense>
                </ErrorBoundary>
              </OrderEntryHostFrame>
            )}
          </div>
          {showLockOverlay && <KdsLockedOverlay />}
        </ErrorProvider>
      </TdsProvider>
    </IdsProvider>
  )
}

export const KitchenDisplay = observer(KitchenDisplayComponent)
